import { FabricCategoryDto } from './../../../models/fabric-categories/fabric-category-dto.interface';
import { Component, Injector, Input, OnInit } from '@angular/core';
import { BaseModalComponent } from '@card-software/library';
import { AssetModel } from '../../../models/AssetModel';
import { ModelProxyService } from '../../../proxies/asset/model.proxy.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AssetType } from '../../../models/AssetType';
import { AssetModelProperty } from '../../../models/AssetModelProperty';
import _ from 'lodash';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { FabricCategoryProxyService } from '../../../proxies/item/fabric-category.proxy.service';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-model-details-modal',
  templateUrl: './model-details-modal.component.html',
  styleUrl: './model-details-modal.component.scss',
})
export class ModelDetailsModalComponent extends BaseModalComponent<AssetModel | null> implements OnInit {
  @Input() model: AssetModel | null = null;
  @Input() types: AssetType[] = [];
  @Input() modelProperties: AssetModelProperty[] = [];

  selectedModelProperties: AssetModelProperty[] = [];
  selectedFabricCategories: FabricCategoryDto[] = [];

  filterFabricCategories: FabricCategoryDto[] = [];
  fabricCategories: FabricCategoryDto[] = [];
  modelProperty: FormControl<AssetModelProperty | null> = new FormControl(null);
  fabricCategory: FormControl<FabricCategoryDto | null> = new FormControl(null);
  assetType: FormControl<AssetType | null> = new FormControl(null, [Validators.required]);
  name: FormControl<string | null> = new FormControl('', [Validators.required]);
  description: FormControl<string | null> = new FormControl('', [Validators.required]);
  propertiesReference: FormControl<string[] | null> = new FormControl([]);
  createdBy: FormControl<string | null> = new FormControl('');
  modifiedBy: FormControl<string | null> = new FormControl('');
  modifiedDate: FormControl<Date> = new FormControl(new Date(), {
    nonNullable: true,
  });

  //Arrays for autocomplete
  filteredTypes: AssetType[] = [];
  filteredModelProperties: AssetModelProperty[] = [];

  formModelGroup: FormGroup = new FormGroup({
    name: this.name,

    description: this.description,

    assetType: this.assetType,
    modelProperty: this.modelProperty,

    createdBy: this.createdBy,
    modifiedBy: this.modifiedBy,

    modifiedDate: this.modifiedDate,
    fabricCategory: this.fabricCategory,
  });

  constructor(
    injector: Injector,
    private fabricCategoryService: FabricCategoryProxyService,
    private readonly modelProxyService: ModelProxyService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.formModelGroup.patchValue({
      id: this.model?.id || '',
      name: this.model?.name || '',
      description: this.model?.description || '',
      assetType: this.types.find((type) => type.id === this.model?.assetType?.id) || null,
      modelProperty: null,
      fabricCategory: null,
      createdBy: this.model?.createdBy || '',
      modifiedBy: this.model?.modifiedBy || '',
      modifiedDate: this.model?.modifiedDate || new Date(),
    });

    this.modelProperty.valueChanges.subscribe((value) => {
      if (typeof value === 'string') return;
      if (value) {
        this.onPropertySelected(value);
      }
    });

    this.fabricCategory.valueChanges.subscribe((value) => {
      if (typeof value === 'string') return;
      if (value) {
        this.onFabricCategorySelected(value);
      }
    });

    this.selectedModelProperties = this.modelProperties.filter((property) =>
      this.model?.modelProperties?.some((p) => p.id === property.id),
    );

    this.fabricCategoryService.getAllFabricCategories().subscribe((response) => {
      if (response.data) {
        this.fabricCategories = response.data;
        if (this.model?.supportedFabricCategories)
          this.selectedFabricCategories = this.fabricCategories.filter((category) =>
            this.model?.supportedFabricCategories?.includes(category.code!),
          );
      }
    });
  }

  get isKnittingMachineAssetType(): boolean {
    return this.assetType.value?.assetCategory?.id === environment.knittingMachineCategoryId;
  }

  fabricCategoriesTypeahead(event: AutoCompleteCompleteEvent) {
    const query = event.query.toLowerCase();
    this.filterFabricCategories = this.fabricCategories
      .filter((t) => t.code?.toLowerCase().includes(query))
      .sort((a, b) => a.code!.localeCompare(b.code!))
      .slice(0, 8);
  }
  onFabricCategorySelected(selectedCategory: FabricCategoryDto) {
    if (!this.selectedFabricCategories.some((c) => c.id === selectedCategory.id)) {
      this.selectedFabricCategories.push(selectedCategory);

      // Filtrar la categoría seleccionada de las disponibles para la búsqueda
      this.fabricCategories = this.fabricCategories.filter((category) => category.id !== selectedCategory.id);
      this.fabricCategory.reset();
    }
  }

  removeFabricCategory(category: FabricCategoryDto) {
    this.selectedFabricCategories = this.selectedFabricCategories.filter((c) => c.id !== category.id);

    if (!this.fabricCategories.some((c) => c.id === category.id)) {
      this.fabricCategories.push(category);
    }
  }

  typesTypeahead(event: AutoCompleteCompleteEvent) {
    const query = event.query.toLowerCase();
    this.filteredTypes = this.types
      .filter((t) => t.name.toLowerCase().includes(query))
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, 8);
  }

  modelPropertyTypeahead(event: AutoCompleteCompleteEvent) {
    const query = event.query.toLowerCase();

    const availableProperties = this.modelProperties.filter(
      (property) => !this.selectedModelProperties.some((selected) => selected.id === property.id),
    );

    this.filteredModelProperties = availableProperties
      .filter((property) => property.name.toLowerCase().includes(query))
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, 8);
  }

  onPropertySelected(selectedProperty: any) {
    if (!this.selectedModelProperties.some((p) => p.id === selectedProperty.id)) {
      this.selectedModelProperties.push(selectedProperty);

      this.modelProperties = this.modelProperties.filter((property) => property.id !== selectedProperty.id);
      this.modelProperty.reset();
    }
  }

  removeProperty(property: AssetModelProperty) {
    this.selectedModelProperties = this.selectedModelProperties.filter((p) => p.id !== property.id);
  }

  onClose() {
    this.name.value;
    this.formModelGroup.controls['name'].value;
    this.close({ action: 'close', data: null });
  }

  isButtonDisabled(): boolean {
    return this.formModelGroup.invalid || typeof this.assetType.value === 'string';
  }

  onSave() {
    if (this.formModelGroup.valid) {
      const currentDate = new Date();
      this.modifiedDate.setValue(currentDate);

      const model: AssetModel = {
        id: this.model?.id ?? '',
        name: this.name.value ?? '',
        description: this.description.value ?? '',
        assetTypeReference: this.assetType?.value?.id ?? '',
        modelPropertiesReferences: this.selectedModelProperties.map((property) => property.id),
        excludedProcessIds: [],
        createdBy: this.createdBy.value ?? '',
        modifiedBy: this.modifiedBy.value ?? '',
        modifiedDate: currentDate,
        supportedFabricCategories: this.selectedFabricCategories.map((property) => property.code!),
      };

      this.modelProxyService.upsertModel(model).subscribe({
        next: (response) => {
          if (response.data) {
            response.data.modelProperties = this.selectedModelProperties ?? [];
            response.data.assetType = this.assetType?.value ?? undefined;
            this.close({ action: 'save', data: response.data });
          }
        },
        error: (error) => {
          console.log('Error saving model', error);
        },
      });
    }
  }
}
